import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { machinePicklistPath } from '@/lib/entityPaths';
import { messageForFailedResponse } from '@/lib/formatApiError';
import {
  canCreateMachinePicklist,
  parseGeneratedPicklistId,
  parseMachinePicklistList,
  picklistHistoryState,
  type MachinePicklistAction,
  type MachinePicklistListItem,
} from '@/lib/machinePicklist';
import {
  isActiveRefillSessionStatus,
  type RefillSessionListItem,
  type RefillSessionListResult,
} from '@/lib/refillSession';

function parseActiveRefill(data: unknown): RefillSessionListItem | null {
  if (data == null || typeof data !== 'object') {
    throw new Error('Invalid refill session list response');
  }
  const items = (data as RefillSessionListResult).items;
  if (!Array.isArray(items)) {
    throw new Error('Invalid refill session list response');
  }
  return (
    items.find(
      (item) =>
        item != null &&
        typeof item.id === 'string' &&
        typeof item.status === 'string' &&
        isActiveRefillSessionStatus(item.status)
    ) ?? null
  );
}

export function useMachinePicklistAction(machineId: string | undefined, unmanaged: boolean) {
  const navigate = useNavigate();
  const [latest, setLatest] = useState<MachinePicklistListItem | null>(null);
  const [activeRefill, setActiveRefill] = useState<RefillSessionListItem | null>(null);
  const [loading, setLoading] = useState(Boolean(machineId));
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!machineId) {
      setLatest(null);
      setActiveRefill(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const latestParams = new URLSearchParams({
        machineId,
        page: '1',
        pageSize: '1',
      });
      const sessionParams = new URLSearchParams({
        machineId,
        filter: 'needs_action',
        page: '1',
        pageSize: '20',
      });

      const [latestRes, sessionsRes] = await Promise.all([
        apiFetch(`/api/v1/picklists?${latestParams}`),
        apiFetch(`/api/v1/refill-sessions?${sessionParams}`),
      ]);

      if (latestRes.status === 403) {
        setLatest(null);
        setActiveRefill(null);
        return;
      }
      if (!latestRes.ok) {
        throw new Error(await messageForFailedResponse(latestRes, 'Failed to load picklists'));
      }
      const latestList = parseMachinePicklistList(await latestRes.json());
      setLatest(latestList.items[0] ?? null);

      if (sessionsRes.status === 403) {
        setActiveRefill(null);
        return;
      }
      if (!sessionsRes.ok) {
        throw new Error(await messageForFailedResponse(sessionsRes, 'Failed to load refill sessions'));
      }
      setActiveRefill(parseActiveRefill(await sessionsRes.json()));
    } catch (err) {
      setLatest(null);
      setActiveRefill(null);
      setError(err instanceof Error ? err.message : 'Failed to load picklists');
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    void load();
  }, [load]);

  const latestIsOpen = latest != null && picklistHistoryState(latest.refillSessionId) === 'open';
  const action: MachinePicklistAction = canCreateMachinePicklist({
    unmanaged,
    hasActiveRefill: activeRefill != null,
    latestIsOpen,
  });

  const generate = useCallback(async () => {
    if (!machineId || action !== 'new') return;
    setGenerating(true);
    setError(null);
    try {
      const response = await apiFetch('/api/v1/picklists/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ machineId, bufferPercent: 10 }),
      });
      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to generate picklist'));
      }
      const picklistId = parseGeneratedPicklistId(await response.json());
      navigate(machinePicklistPath(machineId, picklistId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate picklist');
    } finally {
      setGenerating(false);
    }
  }, [action, machineId, navigate]);

  const continuePicklist = useCallback(() => {
    if (!machineId || !latest || action !== 'continue') return;
    navigate(machinePicklistPath(machineId, latest.id));
  }, [action, latest, machineId, navigate]);

  return {
    action,
    latest,
    activeRefill,
    loading,
    generating,
    error,
    generate,
    continuePicklist,
  };
}
